"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Star,
  MapPin,
  Waves,
  Heart,
  Share,
  Navigation,
  Users,
  Calendar,
  MessageCircle,
  TrendingUp,
  Sparkles,
  Info,
  Lock,
} from "lucide-react"
import { ForecastOverview } from "@/components/forecast-overview"
import { SurfPartyList } from "@/components/surf-party-list"
import { CreatePartyModal } from "@/components/create-party-modal"
import { PartyDetailModal } from "@/components/party-detail-modal"
import { SpotSurfPlans } from "@/components/spot-surf-plans"
import { SpotChat } from "@/components/spot-chat"
import { AggregatedSpotAttributes } from "@/components/aggregated-spot-attributes"
import { AddReviewModal } from "@/components/add-review-modal"
import { SpotImageVoting } from "@/components/spot-image-voting"
import { AppLoadingShell } from "@/components/app-loading-shell"
import { AuthWall } from "@/components/auth-wall"
import { Paywall } from "@/components/paywall"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { useProfile } from "@/hooks/use-profile"
import { useState, useEffect, useCallback } from "react"
import type React from "react"
import { createClient } from "@/lib/supabase/client"
import type { SurfSpot } from "@/lib/types/surf-spot"
import { useParams, useRouter, useSearchParams } from "next/navigation"
import { useAuth } from "@/lib/auth/context"
import { cn } from "@/lib/utils"
import Link from "next/link"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"

export default function SpotDetailPage() {
  const { isMobile, isLoaded } = usePlatform()
  const params = useParams()
  const spotSlug = params.slug as string
  const router = useRouter()
  const searchParams = useSearchParams()
  const [surfSpot, setSurfSpot] = useState<SurfSpot | null>(null)
  const [loading, setLoading] = useState(true)
  const [insightsRefreshTrigger, setInsightsRefreshTrigger] = useState(0)
  const [showCreatePartyModal, setShowCreatePartyModal] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [showAuthWallModal, setShowAuthWallModal] = useState(false)
  const [showPaywallModal, setShowPaywallModal] = useState(false)
  const [selectedPartyId, setSelectedPartyId] = useState<string | null>(null)
  const [partyListKey, setPartyListKey] = useState(0)
  const [forecastFallback, setForecastFallback] = useState(false)
  const [isFavorited, setIsFavorited] = useState(false)
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false)
  const [userProfileId, setUserProfileId] = useState<string | null>(null)
  const [totalMembers, setTotalMembers] = useState(1)
  const [monthlyMembers, setMonthlyMembers] = useState(0)
  const { user } = useAuth()
  const { profile } = useProfile()

  const isPlus = profile?.subscription_status === 'active' || 
                 profile?.subscription_tier === 'plus_lifetime' || 
                 profile?.subscription_tier === 'plus_yearly' || 
                 profile?.subscription_tier === 'free_lifetime_earlybird'

  const handleActionClick = () => {
    if (!user) {
      setShowAuthWallModal(true)
    } else if (!isPlus) {
      setShowPaywallModal(true)
    }
  }

  useEffect(() => {
    // Scroll to top immediately when slug changes (for navigation between spots)
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    
    if (spotSlug) {
      fetchSurfSpot()
    }
  }, [spotSlug])

  // Refresh insights when returning from review page
  useEffect(() => {
    const reviewSubmitted = searchParams.get('reviewSubmitted')
    if (reviewSubmitted === 'true') {
      // Trigger refresh of insights
      setInsightsRefreshTrigger(prev => prev + 1)
      // Clean up URL parameter
      router.replace(`/spot/${spotSlug}`, { scroll: false })
    }
  }, [searchParams, router, spotSlug])

  useEffect(() => {
    // Scroll to top when surfSpot data loads (ensures we're at top after content renders)
    if (surfSpot) {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
      }, 0)
    }
  }, [surfSpot])

  // Fetch user profile ID from users table
  useEffect(() => {
    const fetchUserProfileId = async () => {
      if (!user?.id) return
      
      const supabase = createClient()
      if (!supabase) return
      try {
        const { data, error } = await supabase
          .from("users")
          .select("id")
          .eq("auth_id", user.id)
          .single()
        
        if (error) {
          console.error("Error fetching user profile ID:", error)
          return
        }
        
        if (data) {
          setUserProfileId(data.id)
        }
      } catch (error) {
        console.error("Error fetching user profile ID:", error)
      }
    }
    
    if (user) {
      fetchUserProfileId()
    }
  }, [user])

  // Fetch favorite status
  useEffect(() => {
    if (userProfileId && surfSpot?.id) {
      fetchFavoriteStatus()
    }
  }, [userProfileId, surfSpot?.id])

  const fetchFavoriteStatus = async () => {
    if (!userProfileId || !surfSpot?.id) return

    const supabase = createClient()
    if (!supabase) return
    try {
      const { data, error } = await supabase
        .from("favorites")
        .select("id")
        .eq("user_id", userProfileId)
        .eq("surf_spot_id", surfSpot.id)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error("Error fetching favorite status:", error)
        return
      }

      setIsFavorited(!!data)
    } catch (error) {
      console.error("Error fetching favorite status:", error)
    }
  }

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!user) {
      triggerHaptic('warning')
      setShowAuthWallModal(true)
      return
    }

    if (!userProfileId || !surfSpot?.id) return

    setIsTogglingFavorite(true)
    const supabase = createClient()
    if (!supabase) return

    try {
      if (isFavorited) {
        const { error } = await supabase
          .from("favorites")
          .delete()
          .eq("user_id", userProfileId)
          .eq("surf_spot_id", surfSpot.id)

        if (error) {
          console.error("Error removing favorite:", error)
          return
        }

        setIsFavorited(false)
      } else {
        const { error } = await supabase.from("favorites").insert({
          user_id: userProfileId,
          surf_spot_id: surfSpot.id,
        })

        if (error) {
          console.error("Error adding favorite:", error)
          return
        }

        setIsFavorited(true)

        // Sync with dashboard favorites in localStorage
        try {
          const stored = localStorage.getItem('weather-dashboard-favorites')
          const currentFavorites = stored ? JSON.parse(stored) : []
          
          // Check if already in dashboard favorites
          const exists = currentFavorites.some((f: any) => f.id === surfSpot.id || f.slug === surfSpot.slug)
          
          if (!exists) {
            const newFav = {
              id: surfSpot.id,
              slug: surfSpot.slug,
              name: surfSpot.name,
              lat: Number(surfSpot.latitude),
              lon: Number(surfSpot.longitude),
              fullName: `${surfSpot.name}, ${surfSpot.location}, ${surfSpot.country}`
            }
            
            // Limit to 3 favorites on dashboard
            const updated = [newFav, ...currentFavorites].slice(0, 3)
            localStorage.setItem('weather-dashboard-favorites', JSON.stringify(updated))
            // Trigger storage event for dashboard to update
            try {
              window.dispatchEvent(new Event('storage'))
            } catch (e) {
              const storageEvent = document.createEvent('Event')
              storageEvent.initEvent('storage', true, true)
              window.dispatchEvent(storageEvent)
            }
          }
        } catch (e) {
          console.warn("Failed to sync favorite to dashboard:", e)
        }
      }
    } catch (error) {
      console.error("Error toggling favorite:", error)
    } finally {
      setIsTogglingFavorite(false)
    }
  }

  // Fetch forecast fallback status
  useEffect(() => {
    if (surfSpot?.id) {
      fetch(`/api/surf-forecast?spotId=${surfSpot.id}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setForecastFallback(data.usedFallback || false)
          }
        })
        .catch(err => {
          console.error('Failed to fetch forecast fallback status:', err)
          setForecastFallback(false)
        })
    }
  }, [surfSpot?.id])

  // Fetch member stats
  useEffect(() => {
    const fetchMemberStats = async () => {
      try {
        const response = await fetch("/api/member-stats")
        const data = await response.json()
        
        if (response.ok && data.success) {
          setTotalMembers(data.total || 1)
          setMonthlyMembers(data.monthly || 0)
        } else {
          // Fallback to default values
          setTotalMembers(1)
          setMonthlyMembers(0)
        }
      } catch (error) {
        console.error("Error fetching member stats:", error)
        // Fallback to default values
        setTotalMembers(1)
        setMonthlyMembers(0)
      }
    }
    
    fetchMemberStats()
  }, [])

  const fetchSurfSpot = async () => {
    try {
      // Use API endpoint instead of direct Supabase client to avoid hanging
      const response = await fetch(`/api/surf-spots/${spotSlug}`)
      
      if (!response.ok) {
        if (response.status === 404) {
          console.error("Surf spot not found:", spotSlug)
        } else {
          console.error("Error fetching surf spot:", response.status, response.statusText)
        }
        setLoading(false)
        return
      }

      const result = await response.json()
      
      if (result.success && result.data?.spot) {
        setSurfSpot(result.data.spot)
      } else {
        console.error("Error fetching surf spot:", result.error || "Unknown error")
      }
    } catch (error) {
      console.error("Error fetching surf spot:", error)
    } finally {
      setLoading(false)
    }
  }

  const handlePartyCreated = () => {
    // Refresh the party list
    setPartyListKey((prev) => prev + 1)
  }


  const handleShare = async () => {
    try {
      const shareData = {
        title: surfSpot?.name || "Surf Spot",
        text: `Check out ${surfSpot?.name}`,
        url: typeof window !== 'undefined' ? window.location.href : ''
      }
      if (navigator.share) {
        await navigator.share(shareData as ShareData)
      } else if (navigator.clipboard && shareData.url) {
        await navigator.clipboard.writeText(shareData.url)
        console.log("Link copied to clipboard")
      }
    } catch (e) {
      console.error("Share failed", e)
    }
  }

  const handleGetDirections = () => {
    if (!surfSpot?.latitude || !surfSpot?.longitude) return
    const url = `https://www.google.com/maps/dir/?api=1&destination=${surfSpot.latitude},${surfSpot.longitude}`
    window.open(url, '_blank')
  }

  const handleWriteReview = () => {
    if (!surfSpot) return

    if (!user || !isPlus) {
      handleActionClick()
      return
    }
    
    triggerHaptic('selection')
    setShowReviewModal(true)
  }

  if (loading) {
    return <AppLoadingShell />
  }

  if (!surfSpot) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Waves className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Surf Spot Not Found</h1>
          <p className="text-muted-foreground">The surf spot you're looking for doesn't exist.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b from-white to-blue-50/50 dark:from-slate-950 dark:to-slate-900 ${isMobile ? "pt-[calc(64px+env(safe-area-inset-top))] pb-24" : "px-4 sm:px-6 md:px-8 pb-8 pt-24 md:pt-44"}`}>
      {/* JSON-LD Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Place",
            "name": surfSpot.name,
            "description": `Surf spot ${surfSpot.name} located in ${surfSpot.location}, ${surfSpot.country}. Check live conditions and community reviews.`,
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": surfSpot.latitude,
              "longitude": surfSpot.longitude
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": surfSpot.location,
              "addressCountry": surfSpot.country
            },
            "additionalProperty": [
              {
                "@type": "PropertyValue",
                "name": "Wave Type",
                "value": surfSpot.wave_type
              },
              {
                "@type": "PropertyValue",
                "name": "Skill Level",
                "value": surfSpot.skill_level
              }
            ]
          })
        }}
      />
      <div className={`${isMobile ? "" : "max-w-7xl mx-auto"}`}>
        <div className={`relative ${isMobile ? "rounded-none h-[40vh]" : "rounded-xl overflow-hidden shadow-lg h-[60vh]"} mb-8 overflow-hidden`}>
          <SpotImageVoting spotId={surfSpot.id} currentUserId={user?.id} country={surfSpot.country} />

          {/* Favorite Star Button - Top Right */}
          <button
            onClick={toggleFavorite}
            disabled={isTogglingFavorite || !user}
            className="absolute top-6 right-6 bg-black/20 hover:bg-black/30 backdrop-blur-sm disabled:opacity-50 z-20 pointer-events-auto p-3 rounded-full transition-transform active:scale-90"
            aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
          >
            <Star
              className={`w-6 h-6 transition-colors ${
                isFavorited ? "fill-yellow-400 text-yellow-400" : "text-white"
              }`}
            />
          </button>

          {/* Overlay with spot info */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex items-end pointer-events-none z-10">
            <div className={`p-4 sm:p-6 ${isMobile ? "pb-6" : "md:p-10"} w-full`}>
              {/* Activity Type Indicators */}
              <div className="flex items-center gap-2 mb-2">
                {(surfSpot as any)?.activity_type ? (
                  <>
                    {((surfSpot as any).activity_type === "both" || (surfSpot as any).activity_type === "surfing") && (
                      <span className="text-2xl md:text-4xl drop-shadow-lg">🏄</span>
                    )}
                    {((surfSpot as any).activity_type === "both" || (surfSpot as any).activity_type === "kitesurfing") && (
                      <span className="text-2xl md:text-4xl drop-shadow-lg">🪁</span>
                    )}
                  </>
                ) : (
                  <span className="text-2xl md:text-4xl drop-shadow-lg">🏄</span>
                )}
              </div>
              
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-1 text-white tracking-tight drop-shadow-md">
                {surfSpot?.name}
              </h1>
              <p className="text-sm sm:text-xl md:text-2xl flex items-center gap-2 mb-3 text-white/90 font-medium drop-shadow-md">
                <MapPin className="w-4 h-4" />
                {surfSpot?.location}, {surfSpot?.country}
              </p>
              
              <div className="flex items-center gap-2 sm:gap-4 flex-wrap mb-4">
                <div className="flex items-center gap-1 text-white bg-black/30 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/10">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-sm font-bold">
                    {surfSpot?.avg_overall_rating ? surfSpot.avg_overall_rating.toFixed(1) : "N/A"}
                  </span>
                  <span className="text-white/70 text-[10px] font-medium">
                    ({surfSpot?.review_count || 0})
                  </span>
                </div>
                <Badge className="bg-red-500/90 hover:bg-red-500 text-white font-bold px-3 py-0.5 rounded-full border-none shadow-lg text-[10px]">
                  {surfSpot?.skill_level || "Any Level"}
                </Badge>
                <Badge variant="secondary" className="bg-white/20 text-white border-white/30 backdrop-blur-md px-3 py-0.5 rounded-full font-bold text-[10px]">
                  {surfSpot?.wave_type || "Unknown"}
                </Badge>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 pointer-events-auto">
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 hover:opacity-90 text-white font-black uppercase tracking-widest text-[10px] h-10 px-4 rounded-xl shadow-xl shadow-pink-500/20 flex-1 sm:flex-none"
                  onClick={handleWriteReview}
                >
                  <Star className="w-3 h-3 mr-2" />
                  Review
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20 font-bold h-10 px-4 rounded-xl backdrop-blur-md flex-1 sm:flex-none" 
                  onClick={handleShare}
                >
                  <Share className="w-3 h-3 mr-2" />
                  Share
                </Button>
                <Button 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-500 text-white font-bold h-10 px-4 rounded-xl shadow-xl shadow-blue-600/20 flex-1 sm:flex-none" 
                  onClick={handleGetDirections}
                >
                  <Navigation className="w-3 h-3 mr-2" />
                  Map
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className={isMobile ? "px-6 space-y-10 pb-20 overflow-visible" : "space-y-12"}>
          {/* Stats Bar */}
          {!user && (
            <div className="flex flex-col items-center gap-4 py-6 px-2 bg-white/50 dark:bg-slate-900/50 rounded-[3rem] border border-slate-100 dark:border-slate-800 mb-4">
              <div className="flex flex-wrap items-center justify-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full text-slate-600 dark:text-slate-300 text-[10px] font-black uppercase tracking-widest shadow-sm">
                  <Users className="w-3.5 h-3.5 text-blue-500" />
                  <span>{totalMembers} Members</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full text-slate-600 dark:text-slate-300 text-[10px] font-black uppercase tracking-widest shadow-sm">
                  <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                  <span>+{monthlyMembers} Joined</span>
                </div>
              </div>
              
              <Link
                href="/auth/register"
                onClick={() => triggerHaptic('success')}
                className="w-full max-w-[240px] flex items-center justify-center gap-2 px-6 h-12 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-blue-600/20 active:scale-95 transition-all"
              >
                <Sparkles className="w-4 h-4 text-yellow-300" />
                <span>Join Community</span>
              </Link>
            </div>
          )}

          {/* 10-Day Forecast Section */}
          <section className="space-y-4">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center">
                  <Waves className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">10-Day Forecast</h2>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Real-time ocean data</p>
                </div>
              </div>
              
              <div className="relative group">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-10 w-10 rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Info className="h-5 w-5 text-slate-400" />
                </Button>
                
                {/* Custom Tooltip */}
                <div className="absolute right-0 top-12 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100]">
                  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl shadow-2xl p-6 w-72">
                    <div className="space-y-4 text-xs">
                      <p className="font-black uppercase tracking-widest text-blue-600">Reading the Chart</p>
                      <div className="space-y-3 font-medium text-slate-600 dark:text-slate-300">
                        <p><span className="text-slate-900 dark:text-white font-bold">Wave Height:</span> Estimated face size</p>
                        <p><span className="text-slate-900 dark:text-white font-bold">Swell Period:</span> Power index (seconds)</p>
                        <p><span className="text-slate-900 dark:text-white font-bold">Arrows:</span> Local wind direction</p>
                        <p><span className="text-slate-900 dark:text-white font-bold">Colors:</span> Green (Epic) → Red (Poor)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {isMobile ? (
              <ForecastOverview 
                spotId={surfSpot.id} 
                usedFallback={forecastFallback} 
                activityType={(surfSpot as any)?.activity_type || null}
                onJoinClick={handleActionClick}
              />
            ) : (
              <Card className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 rounded-3xl shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden">
                <CardContent className="p-4 sm:p-6">
                  <ForecastOverview 
                    spotId={surfSpot.id} 
                    usedFallback={forecastFallback} 
                    activityType={(surfSpot as any)?.activity_type || null}
                    onJoinClick={handleActionClick}
                  />
                </CardContent>
              </Card>
            )}
          </section>

          {/* Community Sections - Now visible to all, but with limited info for guests */}
          <div className="space-y-10">
            {/* Surf Parties Section */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 px-1">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-2xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Surf Parties</h2>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Connect with local riders</p>
                </div>
              </div>
              <Card className={`bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 ${isMobile ? "rounded-[3rem]" : "rounded-3xl"} shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden`}>
                <CardContent className="p-8">
                  <SurfPartyList
                    key={partyListKey}
                    spotId={surfSpot.id}
                    onCreateParty={handleActionClick}
                    onPartyClick={(partyId) => {
                      if (!user || !isPlus) {
                        handleActionClick()
                      } else {
                        setSelectedPartyId(partyId)
                      }
                    }}
                    user={user}
                    isPlus={isPlus}
                  />
                </CardContent>
              </Card>
            </section>

            {/* Surf Plans Section */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 px-1">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-2xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Who's Surfing When</h2>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Planned sessions</p>
                </div>
              </div>
              <Card className={`bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 ${isMobile ? "rounded-[3rem]" : "rounded-3xl"} shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden`}>
                <CardContent className="p-8">
                  <SpotSurfPlans spotId={surfSpot.id} user={user} isPlus={isPlus} onJoinPlan={handleActionClick} />
                </CardContent>
              </Card>
            </section>

            {/* Spot Chat */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 px-1">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-2xl flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Spot Discussion</h2>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Community chat</p>
                </div>
              </div>
              <Card className={`bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 ${isMobile ? "rounded-[3rem]" : "rounded-3xl"} shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden`}>
                <CardContent className="p-0">
                  <SpotChat spotId={surfSpot.id} user={user} isPlus={isPlus} onInputClick={handleActionClick} />
                </CardContent>
              </Card>
            </section>
          </div>

          {/* About Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-3 px-1">
              <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center">
                <Info className="w-6 h-6 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">About This Spot</h2>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Description & Vibe</p>
              </div>
            </div>
            <Card className={`bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 ${isMobile ? "rounded-[3rem]" : "rounded-3xl"} shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden`}>
              <CardContent className="p-8">
                <div className="text-slate-700 dark:text-slate-300 leading-relaxed text-base font-medium whitespace-pre-wrap">
                  {surfSpot.description || "No description available for this surf spot."}
                </div>

                <div className="flex flex-wrap gap-2 mt-8">
                  {surfSpot.wave_type && (
                    <Badge variant="secondary" className="bg-blue-50/50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 border-blue-100 dark:border-blue-800 rounded-xl py-2 px-4 font-bold uppercase tracking-wider text-[10px]">
                      {surfSpot.wave_type}
                    </Badge>
                  )}
                  {surfSpot.skill_level && (
                    <Badge variant="secondary" className="bg-orange-50/50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300 border-orange-100 dark:border-orange-800 rounded-xl py-2 px-4 font-bold uppercase tracking-wider text-[10px]">
                      {surfSpot.skill_level}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Community Insights / Reviews */}
          <section className="space-y-4">
            <div className="flex items-center gap-3 px-1">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Community Insights</h2>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Member Reviews</p>
              </div>
            </div>
            <AggregatedSpotAttributes 
              spotId={surfSpot.id} 
              user={user}
              refreshTrigger={insightsRefreshTrigger}
              onWriteReview={handleWriteReview}
              isMobile={isMobile}
            />
          </section>

          <div className={`grid grid-cols-1 ${isMobile ? "gap-10" : "lg:grid-cols-2 gap-10"}`}>
            {/* Best Conditions */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 px-1">
                <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-2xl flex items-center justify-center">
                  <Waves className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Best Conditions</h2>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Ideal Parameters</p>
                </div>
              </div>
              <Card className={`bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 ${isMobile ? "rounded-[3rem]" : "rounded-3xl"} shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden relative`}>
                <CardContent className={cn("p-8 space-y-5", !isPlus && "blur-md pointer-events-none")}>
                  {[
                    { label: "Swell Direction", value: surfSpot.best_swell_direction },
                    { label: "Wind Direction", value: surfSpot.best_wind_direction },
                    { label: "Wave Type", value: surfSpot.wave_type },
                    { label: "Skill Level", value: surfSpot.skill_level }
                  ].map((item, idx) => item.value && (
                    <div key={idx} className="flex justify-between items-center py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
                      <span className="text-sm text-slate-500 dark:text-slate-400 font-black uppercase tracking-widest text-[10px]">{item.label}</span>
                      <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{item.value}</span>
                    </div>
                  ))}
                </CardContent>
                {!isPlus && (
                  <div className="absolute inset-0 flex items-center justify-center p-6">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleActionClick}
                      className="text-[10px] font-black uppercase tracking-widest text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      <Lock className="w-3 h-3 mr-1.5" />
                      {user ? "Upgrade to Unlock Intel" : "Join to Unlock Intel"}
                    </Button>
                  </div>
                )}
              </Card>
            </section>

            {/* Location */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 px-1">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Location</h2>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Getting There</p>
                </div>
              </div>
              <Card className={`bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 ${isMobile ? "rounded-[3rem]" : "rounded-3xl"} shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden`}>
                <CardContent className="p-8 space-y-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-slate-100 dark:border-slate-800">
                      <span className="text-sm text-slate-500 dark:text-slate-400 font-black uppercase tracking-widest text-[10px]">Coordinates</span>
                      <span className="text-sm font-bold text-slate-900 dark:text-slate-100 tracking-tighter">
                        {surfSpot.latitude.toFixed(4)}, {surfSpot.longitude.toFixed(4)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-slate-100 dark:border-slate-800">
                      <span className="text-sm text-slate-500 dark:text-slate-400 font-black uppercase tracking-widest text-[10px]">Region</span>
                      <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{surfSpot.location}</span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-sm text-slate-500 dark:text-slate-400 font-black uppercase tracking-widest text-[10px]">Country</span>
                      <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{surfSpot.country}</span>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleGetDirections} 
                    className="w-full h-14 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black uppercase tracking-widest text-xs rounded-2xl shadow-xl hover:scale-[1.02] transition-transform mt-2"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Open in Apple Maps
                  </Button>
                </CardContent>
              </Card>
            </section>
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreatePartyModal
        open={showCreatePartyModal}
        onOpenChange={setShowCreatePartyModal}
        spotId={surfSpot.id}
        onPartyCreated={handlePartyCreated}
      />

      <PartyDetailModal
        open={selectedPartyId !== null}
        onOpenChange={(open) => !open && setSelectedPartyId(null)}
        partyId={selectedPartyId}
      />

      <AddReviewModal
        open={showReviewModal}
        onOpenChange={setShowReviewModal}
        spotId={surfSpot.id}
        spotName={surfSpot.name}
        spotLocation={surfSpot.location}
        onReviewAdded={() => {
          setInsightsRefreshTrigger(prev => prev + 1)
        }}
      />

      <Dialog open={showAuthWallModal} onOpenChange={setShowAuthWallModal}>
        <DialogContent className="p-0 border-none bg-transparent shadow-none max-w-sm">
          <DialogTitle className="sr-only">Authentication Required</DialogTitle>
          <DialogDescription className="sr-only">Join the community to unlock this feature.</DialogDescription>
          <AuthWall 
            variant="card"
            title="Community Access" 
            description="Join our surf community to unlock parties, spot chats, and member session logs."
            className={isMobile ? "rounded-[3rem]" : "rounded-3xl"}
          />
        </DialogContent>
      </Dialog>

      <Paywall 
        open={showPaywallModal} 
        onOpenChange={setShowPaywallModal} 
        price={profile?.payment_amount_cents ? `€${(profile.payment_amount_cents / 100).toFixed(2)}` : "€2.50"}
        tier={profile?.subscription_tier === 'plus_lifetime' ? 'lifetime' : 'yearly'}
      />
    </div>
  )
}